/**
 * TaskTree — recursive task tree for the Projects feature.
 *
 * Renders the hierarchical list returned by ``GET /api/projects/{id}/tree``
 * and exposes callbacks for every mutating action (add child, edit, delete,
 * reparent, link habit/timer). The component stays presentational: it owns
 * no data-fetching state. Parent pages pass in the tree and a ``refresh``
 * callback, and we call back up on each action so the parent can reload.
 *
 * The depth cap (5 levels) mirrors the backend's ``MAX_TASK_DEPTH``; we
 * disable the "Add Subtask" button at the leaf level so users don't get
 * a 400 from the server.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BlockIcon from '@mui/icons-material/Block';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

export type TaskStatus =
  | 'todo'
  | 'in_progress'
  | 'blocked'
  | 'done'
  | 'cancelled';

export interface TaskTreeNodeData {
  id: number;
  user_id: number;
  project_id: number;
  parent_task_id: number | null;
  title: string;
  description?: string | null;
  status: TaskStatus;
  progress: number;
  is_auto_progress: boolean;
  due_date?: string | null;
  completed_at?: string | null;
  position: number;
  habit_id?: number | null;
  life_timer_id?: number | null;
  work_timer_id?: number | null;
  // Planning metadata (migration 0033). Null throughout means "not set":
  // an unestimated task is unestimated, not a zero-minute one, and an unrated
  // task is unrated rather than middling.
  estimated_minutes?: number | null;
  priority?: number | null;
  impact?: number | null;
  chunk_min_minutes?: number | null;
  chunk_max_minutes?: number | null;
  required_hour_class_id?: number | null;
  created_at: string;
  updated_at?: string | null;
  depth?: number | null;
  children: TaskTreeNodeData[];
}

export interface TaskTreeProps {
  nodes: TaskTreeNodeData[];
  /** Hard cap enforced server-side; clients disable "add subtask" at this depth. */
  maxDepth?: number;
  onAddChild: (parent: TaskTreeNodeData | null) => void;
  onEdit: (task: TaskTreeNodeData) => void;
  onDelete: (task: TaskTreeNodeData) => void;
  onMove: (task: TaskTreeNodeData) => void;
  onLink: (task: TaskTreeNodeData) => void;
  onToggleStatus: (task: TaskTreeNodeData) => void;
}

const statusMeta: Record<
  TaskStatus,
  {
    label: string;
    color: 'default' | 'primary' | 'success' | 'warning' | 'error';
    icon: React.ReactNode;
  }
> = {
  todo: {
    label: 'To do',
    color: 'default',
    icon: <RadioButtonUncheckedIcon fontSize="small" />,
  },
  in_progress: {
    label: 'In progress',
    color: 'primary',
    icon: <PlayArrowIcon fontSize="small" />,
  },
  blocked: {
    label: 'Blocked',
    color: 'warning',
    icon: <BlockIcon fontSize="small" />,
  },
  done: {
    label: 'Done',
    color: 'success',
    icon: <CheckCircleOutlineIcon fontSize="small" />,
  },
  cancelled: {
    label: 'Cancelled',
    color: 'error',
    icon: <BlockIcon fontSize="small" />,
  },
};

const formatDueDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/** 600 → "10h", 210 → "3h 30m", 45 → "45m"; unset or non-positive → null. */
const formatEstimate = (minutes?: number | null): string | null => {
  if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) return null;
  const whole = Math.round(minutes);
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  if (!hours) return `${rest}m`;
  if (!rest) return `${hours}h`;
  return `${hours}h ${rest}m`;
};

/**
 * Compact Eisenhower read-out: "P4 × I2".
 *
 * Returns null when neither side is rated so the row stays quiet; a half-rated
 * task shows the missing side as a dash rather than inventing a middle value.
 */
const formatRating = (
  priority?: number | null,
  impact?: number | null,
): string | null => {
  if (priority == null && impact == null) return null;
  return `P${priority ?? '–'} × I${impact ?? '–'}`;
};

const describeRating = (
  priority?: number | null,
  impact?: number | null,
): string =>
  `Priority ${priority ?? 'unrated'}, impact ${impact ?? 'unrated'} (1–5)`;

interface TaskRowProps extends Omit<TaskTreeProps, 'nodes'> {
  node: TaskTreeNodeData;
  depth: number;
}

const TaskRow: React.FC<TaskRowProps> = ({
  node,
  depth,
  maxDepth = 5,
  onAddChild,
  onEdit,
  onDelete,
  onMove,
  onLink,
  onToggleStatus,
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const canAddChild = depth + 1 < maxDepth;
  const statusInfo = statusMeta[node.status];
  const due = formatDueDate(node.due_date);
  const estimate = formatEstimate(node.estimated_minutes);
  const rating = formatRating(node.priority, node.impact);
  const overdue =
    !!node.due_date &&
    node.status !== 'done' &&
    node.status !== 'cancelled' &&
    new Date(node.due_date).getTime() < Date.now();

  return (
    <Box
      data-testid={`task-row-${node.id}`}
      sx={{
        borderLeft: depth > 0 ? '2px solid' : 'none',
        borderLeftColor: 'divider',
        pl: depth > 0 ? 2 : 0,
        ml: depth > 0 ? 1 : 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          opacity: node.status === 'cancelled' ? 0.55 : 1,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setExpanded((v) => !v)}
          disabled={!hasChildren}
          sx={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.title}`}
        >
          {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>

        <Tooltip title={`Toggle ${node.status === 'done' ? 'incomplete' : 'done'}`}>
          <IconButton
            size="small"
            onClick={() => onToggleStatus(node)}
            color={node.status === 'done' ? 'success' : 'default'}
            aria-label={`Toggle ${node.title} complete`}
          >
            {statusInfo.icon}
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                textDecoration:
                  node.status === 'done' || node.status === 'cancelled'
                    ? 'line-through'
                    : 'none',
              }}
            >
              {node.title}
            </Typography>
            <Chip
              label={statusInfo.label}
              color={statusInfo.color}
              size="small"
              variant={node.status === 'todo' ? 'outlined' : 'filled'}
            />
            {due && (
              <Tooltip title={overdue ? `Overdue — was due ${due}` : `Due ${due}`}>
                <Chip
                  label={`Due ${due}`}
                  size="small"
                  variant="outlined"
                  color={overdue ? 'error' : 'default'}
                />
              </Tooltip>
            )}
            {/* Planning metadata stays deliberately sparse: only a field the
                user actually set earns a chip on these already-dense rows. */}
            {estimate && (
              <Tooltip title={`Estimated ${estimate}`}>
                <Chip label={estimate} size="small" variant="outlined" />
              </Tooltip>
            )}
            {rating && (
              <Tooltip title={describeRating(node.priority, node.impact)}>
                <Chip label={rating} size="small" variant="outlined" />
              </Tooltip>
            )}
            {node.habit_id != null && (
              <Tooltip title={`Linked habit #${node.habit_id}`}>
                <Chip label="Habit" size="small" icon={<LinkIcon />} />
              </Tooltip>
            )}
            {node.life_timer_id != null && (
              <Tooltip title={`Linked life timer #${node.life_timer_id}`}>
                <Chip label="Life Timer" size="small" icon={<LinkIcon />} />
              </Tooltip>
            )}
            {node.work_timer_id != null && (
              <Tooltip title={`Linked work timer #${node.work_timer_id}`}>
                <Chip label="Work Timer" size="small" icon={<LinkIcon />} />
              </Tooltip>
            )}
            {node.is_auto_progress && hasChildren && (
              <Tooltip title="Progress rolls up from subtasks">
                <Chip label="Auto" size="small" variant="outlined" />
              </Tooltip>
            )}
          </Stack>
          {node.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {node.description}
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Box sx={{ flexGrow: 1, maxWidth: 320 }}>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, node.progress || 0))}
                color={node.status === 'done' ? 'success' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {node.progress}%
            </Typography>
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
          <Tooltip
            title={
              canAddChild
                ? 'Add subtask'
                : `Maximum depth (${maxDepth} levels) reached`
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={() => onAddChild(node)}
                disabled={!canAddChild}
                aria-label={`Add subtask under ${node.title}`}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Link habit or timer">
            <IconButton
              size="small"
              onClick={() => onLink(node)}
              aria-label={`Link ${node.title} to a habit or timer`}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Move">
            <IconButton
              size="small"
              onClick={() => onMove(node)}
              aria-label={`Move ${node.title}`}
            >
              <DriveFileMoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(node)}
              aria-label={`Edit ${node.title}`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(node)}
              aria-label={`Delete ${node.title}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {hasChildren && (
        <Collapse in={expanded} unmountOnExit>
          <Box>
            {node.children.map((child) => (
              <TaskRow
                key={child.id}
                node={child}
                depth={depth + 1}
                maxDepth={maxDepth}
                onAddChild={onAddChild}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
                onLink={onLink}
                onToggleStatus={onToggleStatus}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const TaskTree: React.FC<TaskTreeProps> = ({
  nodes,
  maxDepth = 5,
  onAddChild,
  onEdit,
  onDelete,
  onMove,
  onLink,
  onToggleStatus,
}) => {
  const total = useMemo(() => {
    const count = (list: TaskTreeNodeData[]): number =>
      list.reduce((sum, n) => sum + 1 + count(n.children), 0);
    return count(nodes);
  }, [nodes]);

  if (nodes.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2" gutterBottom>
          No tasks yet — add one to break this project down.
        </Typography>
        <IconButton color="primary" onClick={() => onAddChild(null)} aria-label="Add first task">
          <AddIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {total} {total === 1 ? 'task' : 'tasks'} (max depth {maxDepth} levels)
      </Typography>
      {nodes.map((node) => (
        <TaskRow
          key={node.id}
          node={node}
          depth={0}
          maxDepth={maxDepth}
          onAddChild={onAddChild}
          onEdit={onEdit}
          onDelete={onDelete}
          onMove={onMove}
          onLink={onLink}
          onToggleStatus={onToggleStatus}
        />
      ))}
    </Box>
  );
};

export default TaskTree;
